/**
 * English and Filipino. Spec 001, contracts/i18n.md.
 *
 * WHERE THE CHOICE LIVES (research R3)
 *
 * A signed-in person's choice is `language` in their auth user metadata, so it follows them to
 * every device and works for a client too, who has no `users` row. A visitor's choice is in
 * localStorage. Neither set: the browser's languages decide, and `fil` or `tl` means Filipino.
 *
 * The switch changes the screen at once. Saving to the account happens after, and a failed save
 * does not undo the switch: the person asked for Filipino, so they see Filipino.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/use-auth'
import { cn } from '@/lib/utils'
import { type Lang, type MessageKey } from './catalogue'
import { browserLang, isLang, translate } from './translate'

export type { Lang, MessageKey } from './catalogue'
export { browserLang, translate } from './translate'

const STORAGE_KEY = 'ms.language'

function storedLang(): Lang | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return isLang(v) ? v : null
  } catch { return null }
}

type I18nValue = {
  lang: Lang
  t: (key: MessageKey, values?: Record<string, string | number>) => string
  setLang: (lang: Lang) => Promise<void>
}

const I18nContext = createContext<I18nValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const accountLang = (user?.user_metadata as Record<string, unknown> | undefined)?.language
  const [lang, setLangState] = useState<Lang>(() => (isLang(accountLang) ? accountLang : storedLang() ?? browserLang()))

  // A sign-in on this device brings the account's choice with it. An account with no choice yet
  // takes the one the person made on this device before signing in, so the next device knows it.
  useEffect(() => {
    if (isLang(accountLang)) { setLangState(accountLang); return }
    const picked = storedLang()
    if (user && picked) {
      void supabase.auth.updateUser({ data: { language: picked } }).then(({ error }) => {
        if (error) console.warn('language: could not save to the account', error.message)
      })
    }
  }, [accountLang, user])

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const setLang = useCallback(async (next: Lang) => {
    setLangState(next)
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* private window: the screen still switches */ }
    if (user && accountLang !== next) {
      const { error } = await supabase.auth.updateUser({ data: { language: next } })
      if (error) console.warn('language: could not save to the account', error.message)
    }
  }, [user, accountLang])

  const value = useMemo<I18nValue>(() => ({
    lang,
    t: (key, values) => translate(lang, key, values),
    setLang,
  }), [lang, setLang])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useT(): I18nValue {
  const v = useContext(I18nContext)
  if (!v) throw new Error('useT() must be used inside <I18nProvider>')
  return v
}

/** Two buttons: English | Filipino. The current one is pressed. */
export function LanguageMenu({ className }: { className?: string }) {
  const { lang, setLang, t } = useT()
  return (
    <div role="group" aria-label={t('lang.label')} className={cn('inline-flex rounded-[10px] border border-token-subtle bg-token-surface p-0.5', className)}>
      {(['en', 'fil'] as const).map((l) => (
        <button
          key={l}
          type="button"
          aria-pressed={lang === l}
          onClick={() => { void setLang(l) }}
          className={cn(
            'rounded-[8px] px-3 py-1.5 text-[12.5px] font-semibold transition-colors',
            lang === l ? 'bg-brand text-brand-fg' : 'text-token-secondary hover:text-token-primary',
          )}
        >
          {t(l === 'en' ? 'lang.en' : 'lang.fil')}
        </button>
      ))}
    </div>
  )
}
